import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';
import { OrderSummary } from './sections/OrderSummary';
import { AddressSection } from './sections/AddressSection';
import { OrderItems } from './sections/OrderItems';
import { TransportationSection } from './sections/TransportationSection';
import { InternationalShippingSummary } from './sections/InternationalShippingSummary';
import {
  useGetCartById,
  usePlaceOrder,
  useOrganizationsContactData,
} from '~/api';
import { seedFromFirstEligible } from '~/hooks/useContactSelection';
import { unmaskPrimaryContact } from '~/api/sellerContacts';
import type { Contact } from '~/utils/contact';
import { useGetTransportVehicleById } from '~/api/facility';
import { SelectContactsModal } from '~/components/common/SelectContactsModal';
import { useMsrIncoTerms, useMsrUnitTypes } from '~/api/msrPurchaseOrder';
import { useMsrBuyerCodeCandidates } from '~/api/users';
import { PurchaseOrderDatesSection } from './sections/PurchaseOrderDatesSection';
import { MsrPurchaseOrderInputsSection } from './sections/MsrPurchaseOrderInputsSection';
import {
  INITIAL_MSR_PURCHASE_ORDER_INPUTS,
  msrInputsIssue,
  toMsrPurchaseOrderInput,
  type MsrPurchaseOrderInputsForm,
} from './sections/msrPurchaseOrderInputs';
import {
  arePurchaseOrderDatesComplete,
  EMPTY_PURCHASE_ORDER_DATES,
  type PurchaseOrderDates,
} from './sections/purchaseOrderDates';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { BackButton } from '~/components/ui';
import type { Location } from '~/types';
import { LogisticsIncoterm } from '~/graphql/graphql';
import { extractGraphQLErrorMessages, sellerLabel } from '~/utils';
import {
  isPlaceOrderDisabled,
  linesWithoutCapacityFor,
  productNamesOf,
} from '~/utils/order';
import {
  vehicleTypeToTransportMode,
  type TransportMode,
} from '~/hooks/useTransportState';
import { isInternationalComplete } from '~/utils/incoterms';
import { formatAddressText } from '~/utils/addressFormat';
import { DeliveryAddressMapToggle } from '~/components/common/DeliveryAddressMapToggle';
import { removeCart } from '~/redux/reducers/cart';

const VALID_INCO_TERMS = ['EXW', 'DAP'] as const;

const toLogisticsIncoterm = (value: string | null): LogisticsIncoterm | null =>
  Object.values(LogisticsIncoterm).find((incoterm) => incoterm === value) ??
  null;

export function Checkout() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { mutate: placeOrder } = usePlaceOrder();
  const { t } = useTranslation(['cart', 'general']);
  const { id: cartId, addressId } = useParams<{
    id: string;
    addressId: string;
  }>();
  const [searchParams] = useSearchParams();
  const incoTerm = searchParams.get('incoTerm');
  const transportVehicleId = searchParams.get('transportVehicleId');

  // Sticky once placement starts — unlike the mutation's own isPending, this never
  // flips back to false on success. isPending flips false the instant the mutation
  // settles (before navigate() has actually unmounted this page), which would
  // briefly re-enable the query below during that gap. placeOrder moves this
  // cart's order out of DRAFT status, so cart(id: ...) can no longer resolve it
  // server-side — a refetch in that gap races the status change and throws
  // "Cart not found" on the backend. Reset on error so a retry can still load
  // fresh cart data.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSelectContactsModal, setShowSelectContactsModal] = useState(false);
  const [purchaseOrderDates, setPurchaseOrderDates] =
    useState<PurchaseOrderDates>(EMPTY_PURCHASE_ORDER_DATES);

  const [msrInputs, setMsrInputs] = useState<MsrPurchaseOrderInputsForm>(
    INITIAL_MSR_PURCHASE_ORDER_INPUTS
  );
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const { data: cartData, isLoading } = useGetCartById(cartId!, {
    enabled: !isSubmitting,
  });
  const cart = cartData?.cart;
  const selectedAddress = cart?.shippingLocation;
  const currentUserId = currentUser != null ? currentUser.id : undefined;
  const msrCodes = useMsrBuyerCodeCandidates(
    currentUser != null ? currentUser.organization.id : undefined,
    currentUserId,
    cart != null && cart.purchaseOrderDatesRequired === true
  );
  const selfCandidate = msrCodes.candidates.find(
    (candidate) => candidate.id === currentUserId
  );
  const selfEmployeeId =
    selfCandidate != null ? selfCandidate.employeeId : undefined;
  const { data: vehicleData, isLoading: isVehicleLoading } =
    useGetTransportVehicleById(transportVehicleId ?? '');
  const msrUnitTypes = useMsrUnitTypes(
    transportVehicleId,
    cart != null && cart.purchaseOrderDatesRequired === true
  );

  const msrIncoTerms = useMsrIncoTerms(
    toLogisticsIncoterm(incoTerm),
    cart != null && cart.isInternational
      ? (cart.incotermDischarge ?? null)
      : null,
    cart != null && cart.purchaseOrderDatesRequired === true
  );

  const sellingOrganizationId = cart?.sellingOrganization.id ?? '';
  const { data: orgContactData, isError: isOrgContactDataError } =
    useOrganizationsContactData(
      sellingOrganizationId ? [sellingOrganizationId] : []
    );
  // Treat a failed fetch as settled too, so a transient error can't leave Place
  // Order permanently disabled: a click then reports the failure.
  const contactsLoaded = orgContactData !== undefined || isOrgContactDataError;
  const organization = orgContactData?.organizationsByIds.find(
    (org) => org.id === sellingOrganizationId
  );
  const eligibleContacts = organization?.sellerContacts ?? [];
  const initialContactIds = Array.from(
    seedFromFirstEligible(
      eligibleContacts,
      cart?.recipients.map((contact) => contact.id) ?? [],
      organization?.notificationPreference.map((contact) => contact.id) ?? []
    )
  );

  if (isLoading || isVehicleLoading) {
    return <div>Loading...</div>;
  }

  const isInternational = Boolean(cart?.isInternational);

  // International orders carry their own incoterms on the cart, so skip the domestic guard.
  if (
    !incoTerm ||
    (!isInternational &&
      !VALID_INCO_TERMS.includes(
        incoTerm as (typeof VALID_INCO_TERMS)[number]
      )) ||
    !transportVehicleId
  ) {
    return <Navigate to="/cart" replace />;
  }

  if (
    transportVehicleId &&
    !isVehicleLoading &&
    !vehicleData?.transportVehicle
  ) {
    return <Navigate to="/cart" replace />;
  }

  if (!cart?.items) {
    return <div>Cart not found</div>;
  }

  const cartItems = cart.items;
  const primaryContact = unmaskPrimaryContact(cart.primaryContact);
  const vehicle = vehicleData?.transportVehicle;
  const vehicleLabel: TransportMode = vehicle
    ? vehicleTypeToTransportMode(vehicle.vehicleType)
    : 'truck';

  const productsNeedingCapacity = productNamesOf(
    linesWithoutCapacityFor(cartItems, transportVehicleId)
  );
  const internationalComplete = isInternationalComplete(cart, isInternational);
  const purchaseOrderDatesRequired = cart.purchaseOrderDatesRequired;
  const purchaseOrderDatesComplete =
    !purchaseOrderDatesRequired ||
    arePurchaseOrderDatesComplete(purchaseOrderDates);
  const msrPurchaseOrderInputsIssue = purchaseOrderDatesRequired
    ? msrInputsIssue(msrInputs, selfEmployeeId)
    : undefined;

  // International selections were persisted on the cart and are validated server-side at placeOrder.
  const handlePlaceOrder = (
    recipientUserIds: string[],
    primaryContactUserId: string
  ) => {
    setIsSubmitting(true);
    placeOrder(
      {
        orderId: cartId!,
        // use selectedAddress?.id ?? addressId ?? '' to guard against a user
        // navigating directly to /cart/:cartId/checkout/address/undefined
        locationId: selectedAddress?.id ?? addressId ?? '',
        incoTerm: incoTerm as LogisticsIncoterm,
        transportVehicleId,
        // Set on the Cart page (persisted via updateCartTarp); just pass it through.
        tarp: cart.tarp,
        recipientUserIds,
        primaryContactUserId,
        ...(purchaseOrderDatesRequired
          ? {
              ...purchaseOrderDates,
              msrPurchaseOrderInput: toMsrPurchaseOrderInput(
                msrInputs,
                selfEmployeeId
              ),
            }
          : {}),
      },
      {
        onSuccess: () => {
          // The placed order no longer qualifies as a DRAFT cart server-side; drop it
          // from Redux immediately rather than waiting on the invalidated query to
          // refetch, so the order builder's cart summary doesn't briefly count it.
          dispatch(removeCart(cartId!));
          toast.success(t('cart.order_placed_success'));
          navigate(`/orders/view/${cartId}`);
        },
        onError: (error) => {
          setIsSubmitting(false);
          toast.error(extractGraphQLErrorMessages(error).join(', '));
        },
      }
    );
  };

  const handlePlaceOrderClick = () => {
    if (!orgContactData) {
      toast.error(t('general:select_contacts_modal.load_failed'));
      return;
    }
    setShowSelectContactsModal(true);
  };

  const handleConfirmContacts = (
    selectedContactIds: string[],
    chosenPrimaryContact: Contact
  ) => {
    setShowSelectContactsModal(false);
    handlePlaceOrder(selectedContactIds, chosenPrimaryContact.id);
  };

  return (
    <div className="space-y-6">
      <div>
        <BackButton link={'/cart'} label={t('cart.back_button_label')} />
        <h1 className="text-2xl sm:text-3xl text-neut-prim mb-2">
          {t('cart.checkout_title')}
        </h1>
        <p className="text-sm text-neut-sec">
          {t('cart.checkout_description')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <AddressSection
            address={selectedAddress as Location}
            title={t('cart.shipping_address')}
            deliveryAddress={
              cart?.deliveryAddress
                ? {
                    label: t('cart.delivery_address.title'),
                    text: formatAddressText(cart.deliveryAddress),
                  }
                : null
            }
            mapToggle={
              <DeliveryAddressMapToggle
                address={
                  cart?.deliveryAddress ?? selectedAddress?.facility.address
                }
              />
            }
          />
          {isInternational ? (
            <InternationalShippingSummary cart={cart} />
          ) : (
            <TransportationSection
              items={cartItems}
              incoTerm={incoTerm as LogisticsIncoterm}
              vehicle={vehicle}
              tarp={cart.tarp}
            />
          )}
          <OrderItems
            title={t('cart.order_items_section_title')}
            items={cartItems}
            selectedTransportVehicleId={transportVehicleId ?? undefined}
            vehicleLabel={vehicleLabel}
            tarp={cart.tarp}
            currency={cart.currency}
            incoTerm={incoTerm}
          />
          {purchaseOrderDatesRequired && (
            <PurchaseOrderDatesSection
              dates={purchaseOrderDates}
              onChange={setPurchaseOrderDates}
            />
          )}
          {purchaseOrderDatesRequired && (
            <MsrPurchaseOrderInputsSection
              value={msrInputs}
              onChange={setMsrInputs}
              candidates={msrCodes.candidates}
              selfEmployeeId={selfEmployeeId}
              isLoading={msrCodes.isLoading}
              isError={msrCodes.isError}
              unitTypes={msrUnitTypes.unitTypes}
              isUnitTypesLoading={msrUnitTypes.isLoading}
              incoTerms={msrIncoTerms.incoTerms}
              isIncoTermsLoading={msrIncoTerms.isLoading}
            />
          )}
        </div>
        <div className="lg:col-span-1">
          <OrderSummary
            items={cartItems}
            supplierName={sellerLabel(cart.sellingOrganization)}
            isPlacingOrder={isSubmitting}
            onPlaceOrder={handlePlaceOrderClick}
            isPlaceOrderDisabled={
              isPlaceOrderDisabled(
                internationalComplete,
                contactsLoaded,
                productsNeedingCapacity
              ) ||
              !purchaseOrderDatesComplete ||
              msrPurchaseOrderInputsIssue != null
            }
            productsNeedingCapacity={productsNeedingCapacity}
            isInternationalIncomplete={!internationalComplete}
            isPurchaseOrderDatesIncomplete={!purchaseOrderDatesComplete}
            msrPurchaseOrderInputsIssue={msrPurchaseOrderInputsIssue}
            currency={cart.currency}
            incoTerm={incoTerm}
          />
        </div>
      </div>

      {showSelectContactsModal && (
        <SelectContactsModal
          isOpen={showSelectContactsModal}
          onClose={() => setShowSelectContactsModal(false)}
          title={t('cart.select_order_contacts_modal.title')}
          subtitle={t('cart.select_order_contacts_modal.subtitle')}
          lockedLabel={t('general:select_contacts_modal.acted_on_order')}
          primaryContactMode="required"
          primaryRequiredMessage={t(
            'cart.select_order_contacts_modal.primary_required'
          )}
          organizationId={sellingOrganizationId}
          contacts={eligibleContacts}
          initialContactIds={initialContactIds}
          lockedContactIds={cart.lockedRecipientIds}
          initialPrimaryContactId={
            primaryContact ? primaryContact.id : undefined
          }
          onConfirm={handleConfirmContacts}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}
